import { Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import type { MovementRow } from '../../api/types';
import { formatKopeks } from '../../lib/money';
import { formatQuantityMilli } from '../../lib/quantity';

const ROW_BORDER = '1px solid rgba(0,0,0,0.06)';

/**
 * One movement behind a grid cell: what left, how much, at the sklad price, out of
 * which накладная (link), where it went (store накладная link or the sklad),
 * who did it and the MoySklad draft it produced. Undone rows never get here.
 */
export function MovementRowCard({ row }: { row: MovementRow }): JSX.Element {
  const { t } = useTranslation();
  const destination = ((): JSX.Element | null => {
    if (row.dest) {
      const label = row.dest.msName
        ? `${row.dest.storeName} (№${row.dest.msName})`
        : row.dest.storeName;
      return row.dest.shipmentId ? (
        <Link to={`/shipments/${row.dest.shipmentId}`}>{label}</Link>
      ) : (
        <span>{label}</span>
      );
    }
    if (row.sklad) return <span>{t('movements.toSklad', { sklad: row.sklad.name })}</span>;
    return null;
  })();
  const line = { display: 'flex', gap: 6, flexWrap: 'wrap' as const, alignItems: 'center' };

  return (
    <div style={{ display: 'flex', gap: 8, padding: '10px 4px', borderTop: ROW_BORDER }}>
      <div style={{ flex: 1, minWidth: 0, display: 'grid', gap: 4 }}>
        <span style={line}>
          <Tag color={row.kind === 'RETURN' ? 'orange' : 'purple'} style={{ margin: 0 }}>
            {t(row.kind === 'RETURN' ? 'movements.returnTag' : 'movements.transferTag')}
          </Tag>
          <Typography.Text strong>{row.item.name}</Typography.Text>
          <Typography.Text>× {formatQuantityMilli(row.quantityMilli)}</Typography.Text>
          {row.item.buyPriceKopeks != null ? (
            <Typography.Text type="secondary" style={{ fontSize: 13 }}>
              {t('movements.atPrice', { price: formatKopeks(row.item.buyPriceKopeks) })}
            </Typography.Text>
          ) : null}
        </span>
        <span style={{ ...line, fontSize: 13 }}>
          <Link to={`/shipments/${row.source.shipmentId}`}>
            {t('movements.docLink', { name: row.source.msName })}
          </Link>
          <span>· {dayjs(row.source.msDate).format('D MMMM YYYY')}</span>
          <span>→</span>
          {destination}
        </span>
        <span style={{ ...line, fontSize: 13 }}>
          <Tag style={{ margin: 0 }}>{row.actor?.userName ?? t('movements.system')}</Tag>
          <span>{dayjs(row.createdAt).format('HH:mm')}</span>
          {row.msReturn ? (
            <Tag color="green" style={{ margin: 0 }}>
              {row.msReturn.href ? (
                <a href={row.msReturn.href} target="_blank" rel="noreferrer">
                  {t('movements.msDraft', { name: row.msReturn.name ?? row.msReturn.id })}
                </a>
              ) : (
                t('movements.msDraft', { name: row.msReturn.name ?? row.msReturn.id })
              )}
            </Tag>
          ) : null}
        </span>
      </div>
      <Typography.Text strong style={{ marginInlineStart: 8, whiteSpace: 'nowrap' }}>
        {row.item.buySumKopeks != null ? (
          formatKopeks(row.item.buySumKopeks)
        ) : (
          <Typography.Text type="warning">{t('movements.noPrice')}</Typography.Text>
        )}
      </Typography.Text>
    </div>
  );
}
